import { Component, Prop, State, Event, Host, h } from '@stencil/core'
import type { EventEmitter } from "@stencil/core"

import type { Rating } from '../../../../../utils/matomo/matomo'

export interface FeedbackEvent {
    bubbleId: string
    rating: Rating
    text: string
    withdrawn: boolean
}
@Component({
    tag: 'c-feedback',
    styleUrl: 'c-feedback.scss',
})
export class CFeedback {
    @Prop() bubbleId: string
    @State() rating: Rating = 0
    @State() text: string = 'Kein Feedback'
    @State() showInput: boolean = false
    @State() isSubmitted: boolean = false
    @Event() feedback: EventEmitter<FeedbackEvent>

    private textAreaRef?: HTMLTextAreaElement
    onThumbsUp() {
        const isWithdrawn = this.rating === 1
        this.rating = isWithdrawn ? 0 : 1
        this.text = isWithdrawn ? 'Kein Feedback' : 'Default positives Feedback'
        this.showInput = true
        requestAnimationFrame(() => {
            if (this.textAreaRef) {
                this.textAreaRef.focus()
            }
        })
    }

    onThumbsDown() {
        const isWithdrawn = this.rating === -1
        this.rating = isWithdrawn ? 0 : -1
        this.text = isWithdrawn ? 'Kein Feedback' : 'Default negatives Feedback'
        this.showInput = true
        requestAnimationFrame(() => {
            if (this.textAreaRef) {
                this.textAreaRef.focus()
            }
        })
    }

    connectedCallback() {
        // Send 0 for all messages with feedback enabled immediately,
        // because in Matomo we need to get the ratio of no feedback / feedback (We don't merge with Cassandra for now)
        this.emitFeedbackEvent(false)
    }

    emitFeedbackEvent(withdrawn: boolean) {
        this.feedback.emit({
            bubbleId: this.bubbleId,
            rating: this.rating,
            text: this.text,
            withdrawn,
        })
    }

    private readonly handleSubmit = (event?: Event) => {
        if (event) {
            event.preventDefault()
        }
        this.emitFeedbackEvent(true)
        this.isSubmitted = true

        setTimeout(() => {
            this.showInput = false
            this.text = ''
        }, 250)
    }

    private readonly handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            this.handleSubmit()
        }
    }

    private readonly adjustTextAreaHeight = () => {
        if (this.textAreaRef) {
            this.textAreaRef.style.height = 'auto'
            this.textAreaRef.style.height = `${this.textAreaRef.scrollHeight}px`
        }
    }

    render() {
        return (
            <Host>
                <div class="feedback-container">
                    <button
                        title="Daumen hoch"
                        class={`thumbs-up ${this.rating === 1 ? 'selected' : ''}`}
                        aria-label={`${
                            this.rating === 1
                                ? 'Positive Bewertung abgegeben für Antwort mit folgendem Text:'
                                : 'Positive Bewertung abgeben für Antwort mit folgendem Text:'
                        }`}
                        aria-describedby={this.bubbleId}
                        onClick={() => (!this.isSubmitted ? this.onThumbsUp() : undefined)}
                        data-analytics-track="true"
                        data-analytics-action="ButtonClicks TCB: Klick"
                        data-analytics-value={this.rating}
                        data-analytics-name="TCB: Feedback Button"
                        data-analytics-category="TCB | ButtonClicks"
                    >
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                            <g id="Layer_1">
                                <path
                                    id="outline"
                                    d="M4,47c-1.1,0-2,0.9-2,2v44c0,1.1,0.9,2,2,2h30c1,0,1.9-0.8,2-1.8c2,0.6,5.6,1.6,10.1,2.6c7.1,1.6,30.3,1.7,31.4,1.7 c0.8,0,1.5-0.1,2.1-0.1c0.4,0,0.8,0,1.1-0.1c5.7-0.1,7.3-4.8,7.3-7.5c0-1.4-0.4-3.3-1.5-4.8c4.9-0.8,6.3-5.2,6.3-7.8 c0-1.6-0.5-3.8-2-5.5c4.1-1.2,5.3-5.1,5.3-7.6c0-1.8-0.6-4.4-2.6-6.1c3.8-1.8,5-6,5-8.7c0-3.5-1.9-9.4-9.2-9.5c-0.2,0-1.1,0-2.4,0 c-3.7,0-14.9,0.2-16.7-0.1c-1-0.8,0.2-4.2,1.4-7.4c2.5-6.7,5.9-16-0.2-23.8c-3.7-4.8-11.8-7.4-15.3-4.9c-2,1.5-2,5.3-1.8,11.3 c0.1,3.2,0.2,6.5-0.3,8.1c-0.7,2.6-10.3,16-18,26.2V49c0-1.1-0.9-2-2-2"
                                />
                            </g>
                            <g id="Layer_2">
                                <path d="M87.7,55.9c0.6-0.1,1.1-0.1,1.5-0.1c6-0.1,6.3-5.4,6.3-6.5c0-1.5-0.5-6.4-6.3-6.5c-0.2,0-1.1,0-2.3,0 c-15.7,0.2-17.3,0-18-0.4c-3.5-1.9-1.9-6.2-0.1-11.2c2.4-6.4,5.3-14.4,0.3-20.9C66.1,6.5,59.7,4.7,58,6c-0.8,0.8-0.6,6-0.7,8.8 c0.2,3.6,0.3,7-0.4,9.1l-21,30.3v36c1.7,0.5,5.6,1.6,10.7,2.7c6.4,1.4,30.5,1.7,30.7,1.7c0.7,0,1.4-0.1,2-0.1 c0.5-0.1,0.9-0.1,1.2-0.1c4-0.1,4.3-3.5,4.3-4.5s-0.3-4.4-4.3-4.5c-0.2-0.1-0.7-0.1-1-0.1c-0.1,0-0.2,0-0.3,0l0.2-3 c0,0,0.1,0,0.2,0c1.5,0,3,0,4.1-0.1c0.5-0.1,0.9-0.1,1.2-0.1c4.5,0,4.8-3.8,4.8-4.9c0-1.1-0.4-4.8-4.8-4.9c-0.2,0-0.5,0-0.9,0 c-0.9,0-1.9-0.1-2.8-0.2l0.3-3c0.5,0.1,1.7,0.1,2.6,0.2c1,0,2.1-0.1,2.9-0.1c0.5-0.1,0.9-0.1,1.2-0.1C92.6,69,93,65.2,93,64 c0-1.1-0.4-4.8-4.8-4.9c-1.1,0-4.9-0.1-4.9-0.1l0.1-3C85,56.1,86.5,56,87.7,55.9z" />
                                <rect x="5" y="50" class="st0" width="28" height="42" />
                            </g>
                        </svg>
                    </button>
                    <button
                        title="Daumen runter"
                        class={`thumbs-down ${this.rating === -1 ? 'selected' : ''}`}
                        aria-label={`${
                            this.rating === -1
                                ? 'Negative Bewertung abgegeben für Antwort mit folgendem Text:'
                                : 'Negative Bewertung abgeben für Antwort mit folgendem Text:'
                        }`}
                        aria-describedby={this.bubbleId}
                        onClick={() => (!this.isSubmitted ? this.onThumbsDown() : undefined)}
                        data-analytics-track="true"
                        data-analytics-action="ButtonClicks TCB: Klick"
                        data-analytics-value={this.rating}
                        data-analytics-name="TCB: Feedback Button"
                        data-analytics-category="TCB | ButtonClicks"
                    >
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                            <g id="Layer_1">
                                <path
                                    id="outline"
                                    d="M4,47c-1.1,0-2,0.9-2,2v44c0,1.1,0.9,2,2,2h30c1,0,1.9-0.8,2-1.8c2,0.6,5.6,1.6,10.1,2.6c7.1,1.6,30.3,1.7,31.4,1.7 c0.8,0,1.5-0.1,2.1-0.1c0.4,0,0.8,0,1.1-0.1c5.7-0.1,7.3-4.8,7.3-7.5c0-1.4-0.4-3.3-1.5-4.8c4.9-0.8,6.3-5.2,6.3-7.8 c0-1.6-0.5-3.8-2-5.5c4.1-1.2,5.3-5.1,5.3-7.6c0-1.8-0.6-4.4-2.6-6.1c3.8-1.8,5-6,5-8.7c0-3.5-1.9-9.4-9.2-9.5c-0.2,0-1.1,0-2.4,0 c-3.7,0-14.9,0.2-16.7-0.1c-1-0.8,0.2-4.2,1.4-7.4c2.5-6.7,5.9-16-0.2-23.8c-3.7-4.8-11.8-7.4-15.3-4.9c-2,1.5-2,5.3-1.8,11.3 c0.1,3.2,0.2,6.5-0.3,8.1c-0.7,2.6-10.3,16-18,26.2V49c0-1.1-0.9-2-2-2"
                                />
                            </g>
                            <g id="Layer_2">
                                <path d="M87.7,55.9c0.6-0.1,1.1-0.1,1.5-0.1c6-0.1,6.3-5.4,6.3-6.5c0-1.5-0.5-6.4-6.3-6.5c-0.2,0-1.1,0-2.3,0 c-15.7,0.2-17.3,0-18-0.4c-3.5-1.9-1.9-6.2-0.1-11.2c2.4-6.4,5.3-14.4,0.3-20.9C66.1,6.5,59.7,4.7,58,6c-0.8,0.8-0.6,6-0.7,8.8 c0.2,3.6,0.3,7-0.4,9.1l-21,30.3v36c1.7,0.5,5.6,1.6,10.7,2.7c6.4,1.4,30.5,1.7,30.7,1.7c0.7,0,1.4-0.1,2-0.1 c0.5-0.1,0.9-0.1,1.2-0.1c4-0.1,4.3-3.5,4.3-4.5s-0.3-4.4-4.3-4.5c-0.2-0.1-0.7-0.1-1-0.1c-0.1,0-0.2,0-0.3,0l0.2-3 c0,0,0.1,0,0.2,0c1.5,0,3,0,4.1-0.1c0.5-0.1,0.9-0.1,1.2-0.1c4.5,0,4.8-3.8,4.8-4.9c0-1.1-0.4-4.8-4.8-4.9c-0.2,0-0.5,0-0.9,0 c-0.9,0-1.9-0.1-2.8-0.2l0.3-3c0.5,0.1,1.7,0.1,2.6,0.2c1,0,2.1-0.1,2.9-0.1c0.5-0.1,0.9-0.1,1.2-0.1C92.6,69,93,65.2,93,64 c0-1.1-0.4-4.8-4.8-4.9c-1.1,0-4.9-0.1-4.9-0.1l0.1-3C85,56.1,86.5,56,87.7,55.9z" />
                                <rect x="5" y="50" class="st0" width="28" height="42" />
                            </g>
                        </svg>
                    </button>
                    {this.showInput && !this.isSubmitted && (
                        <form class="feedback-form" onSubmit={this.handleSubmit}>
                            <textarea
                                id="textArea"
                                onKeyDown={this.handleKeyDown}
                                ref={(el) => {
                                    this.textAreaRef = el as HTMLTextAreaElement
                                }}
                                class="feedback-input"
                                onInput={(e) => {
                                    this.text = (e.target as HTMLTextAreaElement).value
                                    this.adjustTextAreaHeight()
                                }}
                                placeholder="Kommentar eintragen (optional)"
                                rows={1}
                                aria-label="Kommentar abgeben (optional)"
                                maxlength="400"
                            />
                        </form>
                    )}
                    {this.showInput && !this.isSubmitted && (
                        <button
                            id="FBSubmit"
                            type="submit"
                            class="submit-button"
                            onClick={this.handleSubmit}
                            aria-label="Feedback absenden"
                            title="Feedback absenden"
                            data-analytics-track="true"
                            data-analytics-action="ButtonClicks TCB: Klick"
                            data-analytics-value={this.text ? this.text + ` || Rating: ` +  this.rating + ` || Submit Feedback`: this.rating + ` Submit Feedback`}
                            data-analytics-name="TCB: Feedback Button"
                            data-analytics-category="TCB | ButtonClicks"
                        >
                            Absenden
                        </button>
                    )}
                </div>
            </Host>
        )
    }
}
